'use client';

import React, { useEffect, useState } from 'react';
import { AuthPageContainer, MoonOneLogo } from '@/app/[locale]/(welcome)/_components';
import { useTranslations } from 'next-intl';
import { SignUpForm } from '@/app/[locale]/(welcome)/auth/(with_banner)/signup/SignUpForm';
import { HelperText } from '@/app/[locale]/(welcome)/account-setup/HelperText';
import * as Yup from 'yup';
import { EMAIL_REGEX, NO_SPACES_REGEX, PASSWORD_REGEX } from '@/utils/regex';
import isEmailUnique from '@/utils/validations/isEmailUnique';
import { Formik } from 'formik';
import '@/utils/validations/register';
import { sendConfirmation } from '@/actions/auth/sendConfirmation';
import { VerifyEmailForm } from '@/app/[locale]/(welcome)/auth/(with_banner)/signup/VerifyEmailForm';
import { checkRegisterOTP } from '@/actions/auth/checkRegisterOTP';
import { SetPasswordForm } from '@/app/[locale]/(welcome)/auth/(with_banner)/signup/SetPasswordForm';
import { register } from '@/actions/auth/register';
import { PageWrapper2VerticalSections } from '@/app/[locale]/(welcome)/auth/_styled';
import { SetupSuccessful } from '@/app/[locale]/(welcome)/account-setup/SetupSuccessful';
import { formatTitle } from '@/utils/formatTitle';
import { HideAbove } from '@/components/Media';
import { useRouter } from '@/i18n/routing';

const steps = {
    initial: 'initial',
    confirmation: 'confirmation',
    setPassword: 'setPassword'
};

export default function SignUpPage() {
    const t = useTranslations();
    const router = useRouter();

    const [step, setStep] = useState(steps.initial);
    const [otpError, setOtpError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [registerModel, setRegisterModel] = useState({
        email: '',
        otp: ''
    });
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);

    const initialFormValues = {
        ...registerModel,
        password: '',
        confirmPassword: '',
        showPassword: false,
        showConfirmPassword: false
    };

    const validationSchemas = {
        initial: Yup.object({
            email: Yup.string().required(t('validation.missingEmail')).matches(EMAIL_REGEX, t('validation.invalidEmail')).test('unique', t('validation.emailAlreadyRegistered'), value => new Promise(resolve => isEmailUnique(value, resolve)))
        }),
        setPassword: Yup.object({
            // @ts-ignore
            password: Yup.string().required(t('validation.setNewPass')).sequence([
                () => Yup.string().min(8, t('validation.passMatchCriteria', { chars: 8 })),
                () => Yup.string().matches(PASSWORD_REGEX, t('validation.passMatchCriteria')),
                () => Yup.string().matches(NO_SPACES_REGEX, t('validation.passSpacesCriteria'))
            ]),
            confirmPassword: Yup.string().required(t('auth.confirmNewPass')).test(
                'passwordsMatch',
                t('validation.passwordsDontMatch'),
                function (value, ctx) {
                    return value === ctx.parent.password;
                }
            )
        })
    };

    const submitHandlers = {
        sendConfirmation: async (values) => {
            setLoading(true);

            const success = await sendConfirmation(values.email).catch(console.error);

            if (success) {
                setRegisterModel({
                    ...registerModel,
                    email: values.email
                });
                setStep(steps.confirmation);
                setLoading(false);
            } else {
                console.error('Failed to send confirmation email');
            }
        },
        resendConfirmation: async () => {
            setLoading(true);
            setOtpError(false);

            const success = await sendConfirmation(registerModel.email).catch(console.error);

            if (!success) {
                console.error('Failed to resend confirmation email');
            }

            setLoading(false);
        },
        confirmEmail: async (code: string) => {
            setLoading(true);

            const success = await checkRegisterOTP(registerModel.email, code).catch(console.error);

            if (success) {
                setRegisterModel({
                    ...registerModel,
                    otp: code
                });
                setStep(steps.setPassword);
            } else {
                setOtpError(true);
            }

            setLoading(false);
        },
        registration: async (values) => {
            setLoading(true);
            const success = await register(registerModel.email, values.password, registerModel.otp).catch(console.error);

            if (success) {
                setSuccessDialogOpen(true);
            } else {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        document.title = formatTitle([
            t('meta.titles.signUp'),
            t('meta.titles.moonOne')
        ]);
    }, [t]);

    return (
        <PageWrapper2VerticalSections>
            <HideAbove md>
                <MoonOneLogo />
            </HideAbove>

            <AuthPageContainer>
                {step === steps.initial && (
                    <Formik
                      initialValues={initialFormValues}
                      onSubmit={submitHandlers.sendConfirmation}
                      validationSchema={validationSchemas.initial}
                    >
                        <SignUpForm />
                    </Formik>
                )}

                {step === steps.confirmation &&
                    <VerifyEmailForm
                      handleResend={submitHandlers.resendConfirmation}
                      goBack={() => {
                          setStep(steps.initial);
                          setOtpError(false);
                      }}
                      email={registerModel.email}
                      otpError={otpError}
                      loading={loading}
                      handleConfirm={submitHandlers.confirmEmail}
                      clearError={() => setOtpError(false)}
                    />}

                {step === steps.setPassword && (
                    <Formik
                      initialValues={initialFormValues}
                      onSubmit={submitHandlers.registration}
                      validationSchema={validationSchemas.setPassword}
                    >
                        {formikProps => <SetPasswordForm loading={loading} {...formikProps} />}
                    </Formik>
                )}

                <SetupSuccessful shown={successDialogOpen} onProceed={() => router.replace('/account-setup')} />
            </AuthPageContainer>

            <HelperText withPrivacyPolicy />
        </PageWrapper2VerticalSections>
    );
}
